import { useMsalAuth } from '@contexts/msal-provider';
import { useCallback, useState } from 'react';

import type { UseLoginResult } from './useLogin.types';

import { getReturnUrl } from './useLogin.utils';

/**
 * Hook for login actions on landing page
 *
 * Features:
 * - Clean wrapper around MsalProvider's login functionality
 * - Automatically checks for returnUrl (set when session expires)
 * - Passes returnUrl as deep link for seamless post-login navigation
 *
 * @returns Login handlers and loading state
 *
 * @example
 * const { handleLogin, handleEmployeeLogin, isLoggingIn } = useLogin();
 *
 * <button onClick={() => handleLogin()}>Customer Login</button>
 * <button onClick={() => handleEmployeeLogin()}>Employee Login</button>
 */
export function useLogin(): UseLoginResult {
  const { login } = useMsalAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLogin = useCallback(async (): Promise<void> => {
    try {
      setIsLoggingIn(true);
      const returnUrl = getReturnUrl();
      await login('b2c', returnUrl);
    } catch (error) {
      console.error('Customer login failed:', error);
    } finally {
      setIsLoggingIn(false);
    }
  }, [login]);

  const handleEmployeeLogin = useCallback(async (): Promise<void> => {
    try {
      setIsLoggingIn(true);
      const returnUrl = getReturnUrl();
      await login('b2e', returnUrl);
    } catch (error) {
      console.error('Employee login failed:', error);
    } finally {
      setIsLoggingIn(false);
    }
  }, [login]);

  return {
    handleLogin,
    handleEmployeeLogin,
    isLoggingIn,
  };
}
